import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";

interface VenueQueueSearchProps {
  readonly query: string;
  readonly onQueryChange: (value: string) => void;
}

/**
 * Search box for the venue approval queue. Filtering is client-side
 * (see `VenueApprovalPage`) because `GET /api/admin/venues/pending`
 * returns a flat, unpaginated array — the pending queue is small enough
 * that fetching everything and filtering in-memory is acceptable.
 */
export function VenueQueueSearch({ query, onQueryChange }: VenueQueueSearchProps) {
  const { t } = useTranslation();

  return (
    <div className="max-w-md space-y-1.5">
      <label htmlFor="venue-queue-search" className="font-sans text-sm font-medium text-foreground">
        {t("venues.search.label")}
      </label>
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          id="venue-queue-search"
          data-testid="venue-queue-search-input"
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={t("venues.search.placeholder")}
          className="pl-9 pr-9"
          autoComplete="off"
        />
        {query.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            data-testid="venue-queue-search-clear"
            onClick={() => onQueryChange("")}
            aria-label={t("venues.search.clear")}
            className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 text-muted-foreground"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
